import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export const SplashScreen: React.FC = () => {
  const [progress, setProgress] = useState(0);
  const [isExiting, setIsExiting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // 1. Progress Bar Logic (Simulate loading data)
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + 2;
      });
    }, 40);

    // 2. Navigation Logic
    const timer = setTimeout(() => {
      setIsExiting(true);
      setTimeout(() => {
        navigate('/overview', { replace: true });
      }, 600); // Wait for fade out
    }, 3200);

    // 3. Dev Skip
    const handleSkip = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && process.env.NODE_ENV === 'development') {
        navigate('/overview', { replace: true });
      }
    };

    window.addEventListener('keydown', handleSkip);
    return () => {
      clearInterval(interval);
      clearTimeout(timer);
      window.removeEventListener('keydown', handleSkip);
    };
  }, [navigate]);

  return (
    <div 
      className={`fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-[#05070A] transition-all duration-700 ease-in-out ${
        isExiting ? 'opacity-0 scale-105' : 'opacity-100 scale-100'
      }`}
    >
      {/* Background Radial Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-emerald-500/10 rounded-full blur-[120px] pointer-events-none" />

      {/* Main Content Container */}
      <div className="relative flex flex-col items-center">
        
        {/* Animated Logo with Glow */}
        <div className="relative mb-8 group">
          <div className="absolute inset-0 bg-emerald-500 blur-2xl opacity-20 group-hover:opacity-40 transition-opacity animate-pulse" />
          <div className="relative w-24 h-24 bg-gradient-to-tr from-emerald-600 to-emerald-400 rounded-2xl flex items-center justify-center shadow-[0_0_40px_-10px_rgba(16,185,129,0.5)]">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
            </svg>
          </div>
        </div>

        {/* Text Branding */}
        <div className="text-center space-y-2 mb-12">
          <h1 className="text-4xl font-black text-white tracking-tighter italic">
            STOCK<span className="text-emerald-500 not-italic">RECOS</span>
          </h1>
          <p className="text-gray-500 text-xs font-bold tracking-[0.3em] uppercase">
            Intelligence in Motion
          </p>
        </div>

        {/* Progress Section */}
        <div className="w-64 space-y-4">
          <div className="h-[2px] w-full bg-gray-800 rounded-full overflow-hidden">
            <div 
              className="h-full bg-emerald-500 transition-all duration-150 ease-out"
              style={{ width: `${progress}%` }}
            />
          </div>
          
          <div className="flex justify-between items-center px-1">
            <span className="text-[10px] text-gray-400 font-mono uppercase tracking-widest">
              {progress < 100 ? 'Analyzing Markets...' : 'Ready'}
            </span>
            <span className="text-[10px] text-emerald-500 font-mono">
              {progress}%
            </span>
          </div>
        </div>
      </div>

      {/* Subtle Bottom Footer */}
      <div className="absolute bottom-12 flex flex-col items-center gap-2">
        <div className="flex items-center gap-2 px-3 py-1 bg-white/5 border border-white/10 rounded-full backdrop-blur-md">
           <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
           <span className="text-[10px] text-gray-300 font-medium tracking-wide">TN Ai CORE ACTIVE</span>
        </div>
        <p className="text-[9px] text-gray-600 uppercase tracking-widest mt-2">
          v1.0.4 • Encrypted Session
        </p>
      </div>

      {/* Dev Skip Hint */}
      {process.env.NODE_ENV === 'development' && (
        <div className="absolute bottom-4 right-4 opacity-30">
          <kbd className="text-[10px] text-white border border-white/20 px-2 py-1 rounded">ESC SKIP</kbd>
        </div>
      )}
    </div>
  );
};

export default SplashScreen;